import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { TeamAnonymizationSource } from "@redotech/redo-model/team";
import { useAiSlidePanel } from "@redotech/redo-web/ai/chat/contexts/ai-slide-panel-context";
import { NotificationBannerItem } from "@redotech/redo-web/arbiter-components/notification/redo-notification-banner";
import { isBeforeNow } from "@redotech/util/date";
import { useContext } from "react";
import { useAskRedoSlidePanel } from "../../ask-redo/context/ask-redo-slide-panel-context";
import { useAskRedoEnabled } from "../../ask-redo/hooks/use-ask-redo-enabled";
import { ActionRequiredSeverity } from "./action-required-notification";

export function useTeamDeletion(): {
  severity: ActionRequiredSeverity;
  item: NotificationBannerItem;
} | null {
  const team = useContext(TeamContext);

  // Open the appropriate AI panel depending on the rollout flag —
  // we don't want this banner to silently keep opening the legacy
  // panel once teams flip to the new Ask Redo UI.
  const { open: openLegacy } = useAiSlidePanel();
  const { open: openAskRedo } = useAskRedoSlidePanel();
  const askRedoEnabled = useAskRedoEnabled();
  const openContactSupport = askRedoEnabled
    ? () => openAskRedo()
    : () => openLegacy();

  const anonymizationDate = team?.teamAnonymization?.anonymizationDate;
  const isShopifyShopRedactSchedule =
    team?.teamAnonymization?.source ===
    TeamAnonymizationSource.ShopifyShopRedact;

  const hasAnonymizationDatePassed =
    !!anonymizationDate && isBeforeNow(new Date(anonymizationDate));

  if (
    !anonymizationDate ||
    hasAnonymizationDatePassed ||
    isShopifyShopRedactSchedule
  ) {
    return null;
  }

  return {
    severity: ActionRequiredSeverity.Critical,
    item: {
      id: "team-deletion",
      type: "error",
      message: `This merchant account is scheduled for deletion on ${new Date(
        anonymizationDate,
      ).toLocaleDateString()} due to inactivity. If you believe this is in error, please let us know.`,
      actions: [
        {
          actionType: "button",
          text: "Contact support",
          hierarchy: "primary",
          onClick: openContactSupport,
        },
      ],
    },
  };
}
